import React, { useCallback, useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput, View, useWindowDimensions } from "react-native";
import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  FileTextIcon,
  PencilSimpleIcon,
  ShieldIcon,
  XIcon,
} from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomHtml from "../../../../../components/common/general/CustomHtml/CustomHtml";
import { colors } from "../../../../../styles/design-system";
import type {
  ActivityDetail,
  CourseEvaluatorReviewSidebarItem,
  PublishManualStep,
  StructuredFieldEdits,
} from "../types/reviewTypes";
import { hasHtmlMarkup } from "../utils/hasHtmlMarkup";
import { AIRewriteActionButton, AIRewriteModal, useAIRewriteSuggestion } from "./AIRewriteControls";
import ActivityPanelTabs from "./ActivityPanelTabs";
import IssueFindingsList from "./IssueFindingsList";

interface RecommendationPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetail: ActivityDetail | null;
  manualSteps?: PublishManualStep[];
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFieldEdits) => void;
}

function RecommendationPanel({
  selectedReviewItem,
  activityDetail,
  manualSteps,
  isExpanded,
  isEditDisabled,
  onSaveEdit,
}: RecommendationPanelProps) {
  const detail = activityDetail;
  const currentContent = detail?.currentDescription ?? "";
  const proposedContent = detail?.recommendedDescription ?? "";
  const hasIssueFindings = Boolean(detail?.issueFindings?.length);
  const [isWhyCardCollapsed, setIsWhyCardCollapsed] = useState(false);
  return (
    <View style={styles.panel}>
      {!isExpanded && (
        <View style={styles.titleGroup}>
          <CustomText
            text={detail?.recommendedTitle ?? selectedReviewItem.title}
            useTranslationText={false}
            size="xl"
            weight="bold"
            style={styles.title}
          />
          {detail?.documentReference && (
            <View style={styles.docRefRow}>
              <FileTextIcon size={12} color={colors.grey[500]} weight="regular" />
              <CustomText
                text={detail.documentReference}
                useTranslationText={false}
                size="xs"
                style={styles.docRefText}
              />
            </View>
          )}
        </View>
      )}

      <ActivityPanelTabs attachments={detail?.attachments} manualSteps={manualSteps}>
        <View style={styles.reviewContent}>
          {/* Why card */}
          {!isExpanded && hasIssueFindings && (
            <View style={styles.whyCard}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Toggle why recommendation details"
                onPress={() => setIsWhyCardCollapsed(!isWhyCardCollapsed)}
                style={styles.whyHeaderRow}>
                <CustomText
                  text="Why we're recommending this"
                  useTranslationText={false}
                  size="s"
                  weight="bold"
                  style={styles.whyHeading}
                />
                {isWhyCardCollapsed ? (
                  <CaretRightIcon size={16} color={colors.grey[600]} weight="bold" />
                ) : (
                  <CaretDownIcon size={16} color={colors.grey[600]} weight="bold" />
                )}
              </Pressable>
              {!isWhyCardCollapsed && (
                <>
                  <IssueFindingsList findings={detail?.issueFindingsDetailed} />
                  {detail?.rubricCode && (
                    <View style={styles.rubricRow}>
                      <ShieldIcon size={12} color={colors.brand[600]} weight="regular" />
                      <CustomText
                        text={`${detail.rubricCode}: ${detail.rubricTitle}`}
                        useTranslationText={false}
                        size="xs"
                        weight="medium"
                        style={styles.rubricLink}
                      />
                      {detail.rubricDescription && (
                        <CustomText
                          text={` \u2014 ${detail.rubricDescription}`}
                          useTranslationText={false}
                          size="xs"
                          style={styles.rubricDescription}
                        />
                      )}
                    </View>
                  )}
                </>
              )}
            </View>
          )}

          {/* Monolithic diff — plain text Current vs Proposed */}
          {currentContent && proposedContent && (
            <MonolithicDiff
              currentContent={currentContent}
              proposedContent={proposedContent}
              isExpanded={isExpanded}
              isEditDisabled={isEditDisabled}
              onSave={(description) => onSaveEdit?.({ description })}
            />
          )}

          {/* Last modified */}
          {detail?.lastModifiedBy && (
            <CustomText
              text={`Last modified by ${detail.lastModifiedBy} on ${detail.lastModifiedAt ?? "unknown date"}`}
              useTranslationText={false}
              size="xs"
              style={styles.lastModified}
            />
          )}
        </View>
      </ActivityPanelTabs>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Monolithic diff (simple Current text | Proposed text)
// ---------------------------------------------------------------------------

interface MonolithicDiffProps {
  currentContent: string;
  proposedContent: string;
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onSave?: (editedContent: string) => void;
}

function MonolithicDiff({ currentContent, proposedContent, isExpanded, isEditDisabled, onSave }: MonolithicDiffProps) {
  const { width } = useWindowDimensions();
  const showTwoColumns = width >= 900;
  const canEdit = Boolean(onSave) && !isEditDisabled;
  const [collapsed, setCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingText, setEditingText] = useState(proposedContent);
  const getRewriteProposedText = useCallback(() => editingText, [editingText]);
  const {
    closeRewriteModal,
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    submitRewrite,
  } = useAIRewriteSuggestion({
    currentText: currentContent,
    disabled: !canEdit,
    getProposedText: getRewriteProposedText,
    onApplyRewrite: setEditingText,
  });

  useEffect(() => {
    setIsEditing(false);
    setCollapsed(false);
    setEditingText(proposedContent);
  }, [proposedContent]);

  useEffect(() => {
    setCollapsed(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const handleEdit = useCallback(() => {
    if (!canEdit) return;
    setEditingText(proposedContent);
    setIsEditing(true);
  }, [canEdit, proposedContent]);
  const handleSave = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(false);
    onSave?.(editingText);
  }, [canEdit, editingText, onSave]);
  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditingText(proposedContent);
  }, [proposedContent]);
  return (
    <View style={[diffStyles.container, isExpanded && diffStyles.containerExpanded]}>
      {/* Headers */}
      <View
        style={[
          diffStyles.headerRow,
          showTwoColumns && !collapsed ? diffStyles.headerRowDesktop : diffStyles.headerRowMobile,
        ]}>
        {!collapsed && (
          <View style={[diffStyles.currentHeader, showTwoColumns && diffStyles.headerHalf]}>
            <CustomText
              text="Current"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={diffStyles.currentHeaderText}
            />
            <Pressable onPress={() => setCollapsed(true)} style={diffStyles.iconBtn}>
              <CaretLeftIcon size={14} color={colors.grey[500]} weight="bold" />
            </Pressable>
          </View>
        )}
        <View style={[diffStyles.proposedHeader, !collapsed && showTwoColumns && diffStyles.headerHalf]}>
          <View style={diffStyles.proposedHeaderLeft}>
            {collapsed && (
              <Pressable onPress={() => setCollapsed(false)} style={diffStyles.iconBtn}>
                <CaretRightIcon size={14} color={colors.success[700]} weight="bold" />
              </Pressable>
            )}
            <CustomText
              text="Proposed"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={diffStyles.proposedHeaderText}
            />
          </View>
          {isEditing ? (
            <View style={diffStyles.editingActions}>
              <Pressable onPress={handleCancel} style={diffStyles.cancelBtn}>
                <XIcon size={12} color={colors.grey[600]} weight="bold" />
                <CustomText text="Cancel" useTranslationText={false} size="xs" style={diffStyles.cancelBtnText} />
              </Pressable>
              <AIRewriteActionButton disabled={!canEdit || isRewriting} onPress={openRewriteModal} />
              <CustomButton
                text="Save"
                useTranslationText={false}
                size="xs"
                styleType="primary"
                leftIcon={<CheckIcon size={12} color={colors.white} weight="bold" />}
                onPress={handleSave}
                style={diffStyles.saveBtnSize}
                textStyle={diffStyles.saveBtnText}
              />
            </View>
          ) : (
            <View style={diffStyles.viewActions}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Edit proposed changes"
                accessibilityState={{ disabled: !canEdit }}
                disabled={!canEdit}
                onPress={handleEdit}
                style={[diffStyles.editBtn, !canEdit && diffStyles.editBtnDisabled]}>
                <PencilSimpleIcon size={14} color={canEdit ? colors.grey[700] : colors.grey[400]} weight="regular" />
                <CustomText
                  text="Edit"
                  useTranslationText={false}
                  size="xs"
                  style={[diffStyles.editBtnText, !canEdit && diffStyles.editBtnTextDisabled]}
                />
              </Pressable>
            </View>
          )}
        </View>
      </View>
      {/* Body */}
      <View
        style={[
          diffStyles.bodyRow,
          showTwoColumns && !collapsed ? diffStyles.bodyRowDesktop : diffStyles.bodyRowMobile,
        ]}>
        {!collapsed && (
          <View
            style={[
              diffStyles.bodyCell,
              showTwoColumns && diffStyles.bodyCellHalf,
              showTwoColumns && diffStyles.bodyCellBorder,
            ]}>
            {hasHtmlMarkup(currentContent) ? (
              <CustomHtml html={currentContent} style={diffStyles.bodyText} />
            ) : (
              <CustomText text={currentContent} useTranslationText={false} size="s" style={diffStyles.bodyText} />
            )}
          </View>
        )}
        <View
          style={[
            diffStyles.bodyCell,
            !collapsed && showTwoColumns && diffStyles.bodyCellHalf,
            isExpanded && diffStyles.bodyCellExpanded,
          ]}>
          {isEditing ? (
            <TextInput
              value={editingText}
              onChangeText={setEditingText}
              multiline
              style={diffStyles.textarea}
              textAlignVertical="top"
            />
          ) : hasHtmlMarkup(proposedContent) ? (
            <CustomHtml html={proposedContent} style={diffStyles.bodyText} />
          ) : (
            <CustomText text={proposedContent} useTranslationText={false} size="s" style={diffStyles.bodyText} />
          )}
        </View>
      </View>
      <AIRewriteModal
        disabled={!canEdit}
        errorMessage={rewriteErrorMessage}
        isRewriting={isRewriting}
        onCancel={closeRewriteModal}
        onRewrite={submitRewrite}
        visible={isRewriteModalVisible}
      />
    </View>
  );
}

const diffStyles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  containerExpanded: { flex: 1, minHeight: 0, borderRadius: 0 },
  headerRow: {},
  headerRowDesktop: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: colors.grey[200] },
  headerRowMobile: { flexDirection: "column", borderBottomWidth: 1, borderBottomColor: colors.grey[200] },
  headerHalf: { flex: 1 },
  currentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  currentHeaderText: { color: colors.grey[700] },
  proposedHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    backgroundColor: "#F3FEE7",
  },
  proposedHeaderLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  proposedHeaderText: { color: colors.success[700] },
  iconBtn: { padding: 4, borderRadius: 4 },
  editBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  editBtnText: { color: colors.grey[700] },
  editBtnDisabled: {
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[200],
    opacity: 0.7,
  },
  editBtnTextDisabled: { color: colors.grey[400] },
  viewActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  editingActions: { flexDirection: "row", alignItems: "center", gap: 6 },
  cancelBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelBtnText: { color: colors.grey[600] },
  saveBtnSize: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  saveBtnText: { color: colors.white },
  bodyRow: {},
  bodyRowDesktop: { flexDirection: "row" },
  bodyRowMobile: { flexDirection: "column" },
  bodyCell: { flex: 1, padding: 24 },
  bodyCellExpanded: { minHeight: 0 },
  bodyCellHalf: { flex: 1 },
  bodyCellBorder: { borderRightWidth: 1, borderRightColor: colors.grey[200] },
  bodyText: { color: colors.grey[700], lineHeight: 22 },
  textarea: {
    minHeight: 200,
    padding: 12,
    fontSize: 14,
    lineHeight: 22,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "Inter",
  },
});

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  reviewContent: {
    gap: 16,
  },
  titleGroup: {
    gap: 4,
  },
  title: {
    color: colors.grey[900],
  },
  docRefRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  docRefText: {
    color: colors.grey[500],
  },
  whyCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 6,
  },
  whyHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 8,
  },
  whyHeading: {
    color: colors.grey[900],
    marginBottom: 0,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  rubricLink: {
    color: colors.brand[600],
  },
  rubricDescription: {
    color: colors.grey[500],
  },
  lastModified: {
    color: colors.grey[400],
  },
});

export default RecommendationPanel;
